import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Custom hook to fetch data from Firestore collections
 * @param {string} collectionName - Name of the Firestore collection
 * @returns {object} - { data: [], loading: boolean, error: null | string }
 */
export const useFirestoreData = (collectionName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const collectionRef = collection(db, collectionName);
        const querySnapshot = await getDocs(collectionRef);
        
        const documents = [];
        querySnapshot.forEach((doc) => {
          documents.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        console.log(`[Firestore] ${collectionName}:`, documents);
        setData(documents);
      } catch (err) {
        const errorMsg = `Error fetching ${collectionName}: ${err.message}`;
        console.error(errorMsg);
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionName]);

  return { data, loading, error };
};

/**
 * Custom hook to fetch multiple collections at once
 * @param {string[]} collectionNames - Array of collection names
 * @returns {object} - { data: {collectionName: []}, loading: boolean, error: null | object }
 */
export const useFirestoreMultiple = (collectionNames) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const allData = {};
        
        for (const collectionName of collectionNames) {
          const collectionRef = collection(db, collectionName);
          const querySnapshot = await getDocs(collectionRef);
          
          const documents = [];
          querySnapshot.forEach((doc) => {
            documents.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          console.log(`[Firestore] ${collectionName}:`, documents);
          allData[collectionName] = documents;
        }
        
        setData(allData);
      } catch (err) {
        const errorObj = { message: err.message, timestamp: new Date().toISOString() };
        console.error('Error fetching Firestore collections:', errorObj);
        setError(errorObj);
      } finally {
        setLoading(false);
      }
    };

    if (collectionNames.length > 0) {
      fetchAllData();
    }
  }, [collectionNames]);

  return { data, loading, error };
};
